import { useQuery } from '@tanstack/react-query';

import type { DisputeStatus } from './generated/enums';
import { apiFetch } from './client';

export const disputesKey = ['dispute'] as const;
export const openDisputesKey = [...disputesKey, 'open'] as const;

/**
 * One argument about one settlement (spec Section 9's dispute log).
 *
 * `garmentName` null means the dispute is about the settlement as a whole rather than one line —
 * the commoner case at a counter, where a customer says the deduction is too much without picking
 * out which garment they mean.
 *
 * `settledAmount` is filled only on `Upheld`. A rejected dispute settled at the original figure,
 * and writing that here would make the two indistinguishable a year later.
 */
export interface Dispute {
  id: number;
  bookingId: number;
  bookingNumber: string | null;
  customerName: string;
  rentalOrderItemId: number | null;
  garmentName: string | null;
  status: DisputeStatus;
  claim: string;
  /** Frozen when it was raised, so correcting the charge does not rewrite what was argued about. */
  disputedAmount: number | null;
  settledAmount: number | null;
  raisedOn: string;
  raisedByName: string | null;
  resolvedOn: string | null;
  resolvedByName: string | null;
  resolution: string | null;
  /** How long it has been open. Null once it is closed — a settled argument is not ageing. */
  daysOpen: number | null;
}

export interface RaiseDisputeRequest {
  bookingId: number;
  claim: string;
  rentalOrderItemId?: number | null;
  disputedAmount?: number | null;
}

export interface ResolveDisputeRequest {
  status: DisputeStatus;
  resolution: string;
  settledAmount?: number | null;
}

/** The worklist: what is still open, longest first. */
export function useOpenDisputes() {
  return useQuery({
    queryKey: openDisputesKey,
    queryFn: () => apiFetch<Dispute[]>('/api/dispute/open'),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/** Every dispute on one booking, open and closed — the history a reopened argument is read from. */
export function useBookingDisputes(bookingId: number | null) {
  return useQuery({
    queryKey: [...disputesKey, 'booking', bookingId],
    queryFn: () => apiFetch<Dispute[]>(`/api/dispute/booking/${bookingId}`),
    enabled: bookingId !== null,
  });
}
